<template>
  <div class="def-entry">
    <template v-for="(item, i) in classified" :key="i">
      <!-- Lemma record: the headword's own dictionary entry (a "reading"). -->
      <section v-if="item.kind === 'lemma'" class="def-lemma">
        <!-- Homograph reading marker: the restated headword + a superscript
             ordinal (BANK¹, BANK²). Word-shaped, so a reading marker can never
             be read as a sense number. Only shown when there is more than one. -->
        <div v-if="item.showOrdinal" class="def-entry-head">
          <span class="def-entry-word"
            >{{ item.data.word }}<sup>{{ item.ordinal }}</sup></span
          >
          <span v-if="ipaText(item.data)" class="def-ipa">{{ ipaText(item.data) }}</span>
          <span v-if="formsText(item.data)" class="def-forms">{{ formsText(item.data) }}</span>
        </div>
        <!-- Single reading: pronunciation + forms only, no ordinal. -->
        <div v-else-if="ipaText(item.data) || formsText(item.data)" class="def-etym-head">
          <span v-if="ipaText(item.data)" class="def-ipa">{{ ipaText(item.data) }}</span>
          <span v-if="formsText(item.data)" class="def-forms">{{ formsText(item.data) }}</span>
        </div>

        <!-- Senses numbered continuously across this reading's pos blocks
             (verb 1,2 → noun 3), restarting per reading. -->
        <div v-for="block in posBlocksWithStart(item.data)" :key="block.bi" class="def-posblock">
          <p class="def-pos-head">{{ block.pos || STRINGS.definitions.unknownPos }}</p>
          <ol class="def-senses">
            <li v-for="(sense, si) in block.senses" :key="si" class="def-sense">
              <span class="def-ord">{{ block.startNum + si }}</span>
              <div class="def-sense-body">
                <!-- Sense labels lead the gloss, differentiated by type — not chrome:
                     grammar (dim italic), register/domain (muted caps), regional
                     (slate-blue caps). Middot-separated; hedges + form-of/alt-of dropped. -->
                <div
                  v-if="
                    grammarLabels(sense).length ||
                    registerLabels(sense).length ||
                    regionalLabels(sense).length
                  "
                  class="def-labels"
                >
                  <span v-for="t in grammarLabels(sense)" :key="`g-${t}`" class="def-grammar">{{
                    t
                  }}</span>
                  <span v-for="t in registerLabels(sense)" :key="`r-${t}`" class="def-register">{{
                    t
                  }}</span>
                  <span v-for="t in regionalLabels(sense)" :key="`p-${t}`" class="def-regional">{{
                    t
                  }}</span>
                </div>
                <p class="def-gloss">{{ sense.definition }}</p>

                <p v-if="crossRef(sense.form_of)" class="def-crossref">
                  <span class="def-crossref-label">{{ STRINGS.definitions.formOf }}</span>
                  <span>{{ crossRef(sense.form_of) }}</span>
                </p>
                <p v-if="crossRef(sense.alt_of)" class="def-crossref">
                  <span class="def-crossref-label">{{ STRINGS.definitions.altOf }}</span>
                  <span>{{ crossRef(sense.alt_of) }}</span>
                </p>

                <SenseExamples
                  v-if="sense.examples && sense.examples.length"
                  :examples="sense.examples"
                />
              </div>
            </li>
          </ol>
        </div>

        <!-- Etymology stays last: definitions lead, origin story follows. -->
        <EtymologyBlock v-if="item.data.etymology_text" :text="item.data.etymology_text" />
      </section>

      <!-- Reference records carry no body here: an inflected form collapses to the
           modal's reference note, and its parent lemma is rendered separately. -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import STRINGS from '@/constants/strings';
import SenseExamples from './SenseExamples.vue';
import EtymologyBlock from './EtymologyBlock.vue';
import { routeSenseTags } from './senseTags';
import {
  isLemmaRecord,
  isReferenceRecord,
  type LemmaRecord,
  type PosBlock,
  type ReferenceRecord,
  type Sense,
} from '@/types/word';

const props = defineProps<{
  records: readonly unknown[];
}>();

type Classified =
  | { kind: 'lemma'; data: LemmaRecord; ordinal: number; showOrdinal: boolean }
  | { kind: 'reference'; data: ReferenceRecord }
  | { kind: 'unknown' };

// Records are dataset-owned JSON (#332); classify by the pos_blocks discriminator
// and drop anything that matches neither shape rather than risk a render error.
// Homograph ordinals are POSITIONAL among lemma records — the `etym` field is
// often null in the data, so it can't be trusted as the reading number.
const classified = computed<Classified[]>(() => {
  const lemmaCount = props.records.filter(isLemmaRecord).length;
  let lemmaIndex = 0;
  return props.records.map(record => {
    if (isLemmaRecord(record)) {
      lemmaIndex += 1;
      return { kind: 'lemma', data: record, ordinal: lemmaIndex, showOrdinal: lemmaCount > 1 };
    }
    if (isReferenceRecord(record)) return { kind: 'reference', data: record };
    return { kind: 'unknown' };
  });
});

// Number senses as a single sequence across a reading's pos blocks, so a verb
// block (1, 2) is followed by a noun block (3) rather than both restarting at 1.
function posBlocksWithStart(
  record: LemmaRecord
): { bi: number; pos: string; senses: readonly Sense[]; startNum: number }[] {
  let n = 1;
  return record.pos_blocks.map((block: PosBlock, bi: number) => {
    const startNum = n;
    n += block.senses.length;
    return { bi, pos: block.pos, senses: block.senses, startNum };
  });
}

function ipaText(record: LemmaRecord): string {
  return (record.ipa ?? []).filter(Boolean).join(', ');
}

function formsText(record: LemmaRecord): string {
  return (record.forms ?? [])
    .map(f => {
      const tags = f.tags && f.tags.length ? ` (${f.tags.join(', ')})` : '';
      return `${f.form}${tags}`;
    })
    .join(' · ');
}

// A sense's raw `tags` array is wiktextract's multi-categorical grab-bag; route
// each tag to its slot (see ./senseTags), rendered as flat differentiated labels.
// Hedges and the crossRef-duplicated form-of/alt-of are dropped.
function grammarLabels(sense: Sense): readonly string[] {
  return routeSenseTags(sense.tags).grammar;
}
function registerLabels(sense: Sense): readonly string[] {
  return routeSenseTags(sense.tags).register;
}
function regionalLabels(sense: Sense): readonly string[] {
  return routeSenseTags(sense.tags).regional;
}

// Sense-level form_of / alt_of are plain string arrays of parent headwords.
function crossRef(words: readonly string[] | undefined): string {
  return (words ?? []).filter(Boolean).join(', ');
}
</script>

<style scoped>
.def-lemma + .def-lemma {
  margin-top: 0.5rem;
  padding-top: 0.375rem;
  border-top: 2px solid var(--color-arena-border);
}

/* Reading heads: homograph marker (word + superscript) vs. single-reading meta. */
.def-entry-head {
  display: flex;
  align-items: baseline;
  gap: 0.6875rem;
  flex-wrap: wrap;
  margin: 1rem 0 0.125rem;
}

.def-etym-head {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
  margin: 0.5rem 0 0.125rem;
}

.def-entry-word {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.375rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-arena-text-bright);
  line-height: 1;
}

.def-entry-word sup {
  font-family: var(--font-mono);
  font-size: 0.46em;
  font-weight: 700;
  color: var(--color-energy-primary);
  vertical-align: super;
  margin-left: 2px;
}

.def-ipa {
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  letter-spacing: 0.02em;
  color: var(--color-arena-text-dim);
}

.def-forms {
  font-family: var(--font-sans);
  font-size: 0.71875rem;
  font-style: italic;
  color: var(--color-arena-text-muted);
}

.def-posblock + .def-posblock {
  margin-top: 0.125rem;
}

.def-pos-head {
  margin: 0.8125rem 0 0;
  font-family: var(--font-sans);
  font-style: italic;
  font-weight: 700;
  font-size: 0.8125rem;
  letter-spacing: 0.02em;
  color: var(--color-energy-secondary);
}

.def-senses {
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
}

.def-sense {
  display: flex;
  gap: 0.8125rem;
  padding: 0.6875rem 0;
  border-bottom: 1px solid rgba(61, 52, 40, 0.5);
}

.def-sense:last-child {
  border-bottom: none;
}

.def-ord {
  flex-shrink: 0;
  width: 23px;
  height: 23px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 0.75rem;
  color: var(--color-arena-text-muted);
  border: 1px solid var(--color-arena-border);
  background: rgba(0, 0, 0, 0.2);
}

.def-sense-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  align-items: flex-start;
}

/* Sense labels: flat and typographic, differentiated by type + colour rather than
   chrome (the design system reserves borders/backgrounds for the letterpress tiles).
   Middot-separated on one line, leading the gloss. */
.def-labels {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  font-family: var(--font-sans);
}

.def-labels > * + *::before {
  content: '·';
  margin: 0 0.4em;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  color: var(--color-arena-text-dim);
}

/* Grammatical annotation — dim italic, lowercase; reads as a dictionary aside. */
.def-grammar {
  font-style: italic;
  font-size: 0.75rem;
  letter-spacing: 0.01em;
  color: var(--color-arena-text-dim);
}

/* Register / domain label — the system's eyebrow micro-label (uppercase, tracked,
   muted), no box. The catch-all slot: register + domain + unknown tail. */
.def-register {
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-arena-text-muted);
}

/* Regional label — same micro-label, tinted slate-blue so a place reads as a place. */
.def-regional {
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-energy-secondary);
}

.def-gloss {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 0.90625rem;
  line-height: 1.5;
  color: rgba(245, 230, 200, 0.92);
}

.def-crossref {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin: 0;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  color: var(--color-arena-text-muted);
}

.def-crossref-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-arena-text-muted);
}
</style>
